import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import HeroVisual from "@/components/HeroVisual";
import EditorialMarquee from "@/components/EditorialMarquee";
import ProductOverview from "@/components/ProductOverview";
import PaymentStrip from "@/components/PaymentStrip";
import ManifestoWow from "@/components/ManifestoWow";
import OrderNowCTA from "@/components/OrderNowCTA";
import ReviewsWidget from "@/components/ReviewsWidget";
import Seo from "@/components/Seo";
import { MaskedLine } from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";

export default function Home() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="text-black">
      <Seo
        title="Peptivance — Research grade peptides · EU"
        description="Onderzoekskwaliteit peptiden (Tirzepatide, Retatrutide, GHK) en bacteriostatisch water — aseptisch verpakt, HPLC gevalideerd, verzonden vanuit Nederland."
        url="https://peptivance.eu/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Peptivance",
          url: "https://peptivance.eu",
          logo: "https://peptivance.eu/logo.png",
          address: {
            "@type": "PostalAddress",
            addressCountry: "NL",
          },
          sameAs: [],
        }}
      />
      {/* HERO — off-white with glow, framed by colored ribbons */}
      {/* Ribbon strip: sits BELOW the fixed nav (announcement 32px + nav 64px = 96px) */}
      <div className="relative pt-[104px] md:pt-[112px]">
        <div
          data-testid="hero-ribbon-top"
          className="w-full h-1.5 flex"
          aria-hidden
        >
          <span className="flex-[3]" style={{ background: "#00529B" }} />
          <span className="flex-[2]" style={{ background: "#D12A2F" }} />
          <span className="flex-[3]" style={{ background: "#5A2A82" }} />
          <span className="flex-[2]" style={{ background: "#007A78" }} />
        </div>
        <div className="w-full h-px bg-neutral-300" />
      </div>

      <section className="relative pt-6 md:pt-20 pb-6 md:pb-12 overflow-hidden bg-[#F7F7F6]">
        {/* Ambient corner tints for color variation */}
        <div
          className="absolute -top-32 -left-32 w-[520px] h-[520px] pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(circle at center, rgba(0,82,155,0.10), transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div
          className="absolute -bottom-32 -right-32 w-[520px] h-[520px] pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(circle at center, rgba(90,42,130,0.10), transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-start">
            <div className="lg:col-span-6 z-10">
              <motion.div
                className="font-mono-label text-[10px] md:text-[11px] mb-3 md:mb-6 flex items-center gap-2 md:gap-3 flex-wrap"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 1 }}
              >
                <span className="inline-flex items-center gap-2 border border-[#00529B]/30 text-[#00529B] px-2.5 py-1 bg-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00529B] animate-pulse" />
                  01
                </span>
                <span className="text-neutral-500">{t("hero_overline")}</span>
                <span className="inline-flex items-center gap-1.5 text-neutral-700">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="#111"><path d="M10 1.5l2.63 5.32 5.87.85-4.25 4.14 1 5.85L10 14.9l-5.25 2.76 1-5.85L1.5 7.67l5.87-.85L10 1.5z"/></svg>
                  <span className="font-semibold">{t("hero_stat_rating")}</span>
                  <span className="text-neutral-500">· {t("hero_stat_reviews")}</span>
                </span>
              </motion.div>

              <h1 className="font-hero text-[32px] sm:text-[56px] md:text-[68px] lg:text-[88px] leading-[1.02] mb-3 md:mb-4">
                <MaskedLine i={0}>{t("hero_l1")}</MaskedLine>
                <MaskedLine i={1}>{t("hero_l2")}</MaskedLine>
                <MaskedLine i={2} className="text-neutral-500">{t("hero_l3")}</MaskedLine>
                <MaskedLine i={3} className="text-neutral-500">{t("hero_l4")}</MaskedLine>
              </h1>

              <motion.p
                className="mt-4 md:mt-8 max-w-md text-neutral-700 text-sm md:text-lg leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
              >
                {t("hero_sub")}
              </motion.p>

              <motion.div
                className="mt-5 md:mt-8 flex flex-wrap gap-3 md:gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.8 }}
              >
                <Link data-testid="hero-cta-shop" to="/shop" className="btn-solid group relative overflow-hidden">
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(circle at 30% 50%, rgba(0,82,155,0.45), transparent 60%)" }}
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <span className="relative flex items-center gap-3">
                    {t("hero_cta_shop")}
                    <motion.span animate={{ x: [0, 5, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>→</motion.span>
                  </span>
                </Link>
                <Link data-testid="hero-cta-about" to="/about" className="btn-sharp">
                  {t("hero_cta_about")}
                </Link>
              </motion.div>

              {/* Live urgency banner */}
              <motion.div
                className="mt-4 md:mt-8 inline-flex items-center gap-3 text-xs md:text-sm text-neutral-700"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                data-testid="hero-urgency"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span>
                  <span className="font-mono-label text-[10px] tracking-[0.2em] text-emerald-700 font-semibold">
                    LIVE ·
                  </span>{" "}
                  {t("hero_urgency")}
                </span>
              </motion.div>
            </div>

            <div className="lg:col-span-6 relative">
              <HeroVisual mouseRef={mouseRef} />
            </div>
          </div>

          {/* Colored stat row — conversion-focused */}
          <div className="mt-8 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-neutral-300">
            {[
              { c: "#00529B", k: t("hero_stat_purity"), v: t("hero_stat_purity_label") },
              { c: "#D12A2F", k: t("hero_stat_dispatch"), v: t("hero_stat_dispatch_label") },
              { c: "#5A2A82", k: t("hero_stat_customers"), v: t("hero_stat_customers_label") },
              { c: "#007A78", k: t("hero_stat_rating"), v: t("hero_stat_reviews") },
            ].map((s, i) => (
              <div
                key={i}
                className="relative flex flex-col justify-between gap-1 md:gap-2 px-3 md:px-4 pt-3 md:pt-6 pb-2 md:pb-3 border-b-2"
                style={{ borderBottomColor: s.c }}
              >
                <span className="font-hero text-2xl md:text-5xl" style={{ color: s.c }}>{s.k}</span>
                <span className="font-mono-label text-[9px] md:text-[10px] text-neutral-600">{s.v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE — cream separator */}
      <EditorialMarquee words={t("marquee").split(" . ")} speed={22} />

      {/* PRODUCT OVERVIEW — light cream with alternating white rows */}
      <ProductOverview products={products} />

      {/* ORDER NOW — bright accent CTA section (moved before Manifest) */}
      <OrderNowCTA products={products} />

      {/* MANIFEST — deep midnight with animation + glow (WOW section) */}
      <ManifestoWow />

      {/* PAYMENTS — clean white background */}
      <PaymentStrip />
    </div>
  );
}
